// Documents on a person's record, the handbooks per role, and the gate panel in
// the invite modal.
//
// Nothing here uploads a file. The OS holds a link and a signature state; the
// documents themselves live in Drive (R9).
import { useId, useState } from 'react';
import { Icon, Badge, Button, Checkbox, Mono, Select } from './primitives';
import { PCard } from './people';
import { DisclosedForm, Eyebrow } from './card';
import { ConfirmDialog } from './dialog';
import { Field, TextInput } from './field';
import { InlineStatus, useToast } from './feedback';
import { orgDateKey } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import {
  addDocument,
  addHandbook,
  bypassGate,
  docLabel,
  requiredPapers,
  updateDocument,
  DOC_TYPES,
  type EmployeeDocument,
  type RoleHandbook,
  type DocType,
  type Gate,
} from '../lib/documents';

// A Drive link, or at least a link: a typo used to become a dead "open" (HRP-14).
const isLink = (v: string) => /^https?:\/\/\S+$/i.test(v.trim());

const ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--hf-space-2) var(--hf-space-3)',
  padding: 'var(--hf-space-2) 0',
  borderBottom: '1px solid var(--hf-rule)',
  fontSize: 'var(--hf-text-sm)',
};
const NOTE: React.CSSProperties = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' };
const OPEN: React.CSSProperties = { color: 'var(--hf-accent-text)', fontSize: 'var(--hf-text-sm)' };

type Signed = 'signed_by_company' | 'signed_by_individual' | 'witnessed';
const partsOf = (docType: DocType): [Signed, string][] =>
  DOC_TYPES.find((t) => t.id === docType)?.triPart
    ? [
        ['signed_by_company', 'company'],
        ['signed_by_individual', 'individual'],
        ['witnessed', 'witness'],
      ]
    : [['signed_by_individual', 'signed']];

/**
 * A list of requirements and whether each is met. The mark is a shape and a
 * word, not a colour alone: a check reads "on file", a cross "missing".
 */
function Checklist({ items }: { items: { label: string; ok: boolean }[] }) {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {items.map(({ label, ok }) => (
        <li
          key={label}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hf-space-2)',
            padding: 'var(--hf-space-1) 0',
            fontSize: 'var(--hf-text-sm)',
            color: ok ? 'var(--hf-ink-2)' : 'var(--hf-ink)',
          }}
        >
          <Icon
            name={ok ? 'check' : 'x'}
            size={12}
            style={{ color: ok ? 'var(--hf-sage-text)' : 'var(--hf-ink-3)', flexShrink: 0 }}
          />
          <span>{label}</span>
          <span className="hf-sr-only">{ok ? '— on file' : '— missing'}</span>
        </li>
      ))}
    </ul>
  );
}

/** A person's paperwork. Links out; never holds the file. */
export function DocumentsTab({
  profileId,
  rows,
  handbooks,
  roleId,
  canEdit,
  onChanged,
}: {
  profileId: string;
  rows: EmployeeDocument[];
  handbooks: RoleHandbook[];
  roleId: string | null;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  // The add form is disclosed on demand: the tab's job is reading what is on
  // file, and it used to lead with a permanent form (HRP-18).
  const [adding, setAdding] = useState(false);
  const opener = useId();
  const [docType, setDocType] = useState<DocType>('offer_letter');
  const [link, setLink] = useState('');
  const [company, setCompany] = useState(false);
  const [individual, setIndividual] = useState(false);
  const [witnessed, setWitnessed] = useState(false);
  const [linkErr, setLinkErr] = useState<string>();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const current = handbooks.find((h) => h.role_id === roleId) ?? null;
  const triPart = DOC_TYPES.find((t) => t.id === docType)?.triPart;
  const papers = requiredPapers(rows, current);
  const onFile = papers.filter((p) => p.ok).length;

  const close = () => {
    setAdding(false);
    setLink('');
    setCompany(false);
    setIndividual(false);
    setWitnessed(false);
    setLinkErr(undefined);
    setErr(null);
  };

  const add = async () => {
    if (!link.trim()) {
      setLinkErr('Paste the Drive link — the document itself stays there.');
      return;
    }
    if (!isLink(link)) {
      setLinkErr('That is not a link. It should start with https://');
      return;
    }
    setLinkErr(undefined);
    if (docType === 'handbook_ack' && !current) {
      setErr('There is no handbook issued for this role yet.');
      return;
    }
    setBusy(true);
    setErr(null);
    const { error } = await addDocument({
      profileId,
      docType,
      driveLink: link,
      versionRef: docType === 'handbook_ack' ? current!.id : null,
      signedByCompany: company,
      signedByIndividual: individual,
      witnessed,
    });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    toast.push({ tone: 'success', message: `Recorded ${docLabel(docType)}.` });
    close();
    onChanged();
  };

  // A signature that arrives later is an update to the document on file, not a
  // second row beside a stale one (HRP-14). It commits on change, like every
  // toggle, and says so; Undo puts it back.
  const setSigned = async (d: EmployeeDocument, field: Signed, to: boolean, label: string) => {
    const { error } = await updateDocument(d.id, { [field]: to });
    if (error) {
      toast.push({
        tone: 'error',
        message: `Couldn’t update ${docLabel(d.doc_type)} — ${error.message}`,
      });
      return;
    }
    toast.push({
      tone: 'success',
      message: `${docLabel(d.doc_type)}: ${label} ${to ? 'marked signed' : 'unmarked'}.`,
      action: {
        label: 'Undo',
        onClick: () =>
          void updateDocument(d.id, { [field]: !to }).then(({ error: undoErr }) => {
            if (undoErr)
              toast.push({ tone: 'error', message: `Couldn’t undo — ${undoErr.message}` });
            onChanged();
          }),
      },
    });
    onChanged();
  };

  return (
    <PCard
      label="Documents"
      right={
        canEdit &&
        !adding && (
          <span id={opener}>
            <Button variant="secondary" size="sm" icon="plus" onClick={() => setAdding(true)}>
              Record document
            </Button>
          </span>
        )
      }
    >
      <p style={{ ...NOTE, margin: '0 0 var(--hf-space-3)' }}>
        Links only — the files stay in Drive.
      </p>

      {/* What this person still owes, from the same three checks the invite gate
          makes. "Nothing recorded yet" said nothing about what was missing. A
          group inside the card, so the flat panel (UI-CONVENTIONS §8). */}
      <div
        className="hf-panel"
        style={{
          padding: 'var(--hf-space-3)',
          marginBottom: 'var(--hf-space-3)',
          borderRadius: 'var(--hf-radius-md)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 'var(--hf-space-2)',
            marginBottom: 'var(--hf-space-2)',
          }}
        >
          <Eyebrow as="h3">Required papers</Eyebrow>
          <Mono dim>
            {onFile} of {papers.length} on file
          </Mono>
        </div>
        <Checklist items={papers} />
      </div>

      {!rows.length && <div style={NOTE}>Nothing recorded yet.</div>}
      {rows.map((d) => (
        <div key={d.id} role="group" aria-label={docLabel(d.doc_type)} style={ROW}>
          <span style={{ color: 'var(--hf-ink-2)', flex: '1 1 12rem', minWidth: 0 }}>
            {docLabel(d.doc_type)}
          </span>
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 'var(--hf-space-2)' }}>
            {partsOf(d.doc_type).map(([field, label]) =>
              canEdit ? (
                <Checkbox
                  key={field}
                  label={label}
                  checked={d[field]}
                  onChange={(to) => void setSigned(d, field, to, label)}
                />
              ) : (
                <Badge key={field} tone={d[field] ? 'success' : 'neutral'} dot={d[field]}>
                  {label}
                  <span className="hf-sr-only">{d[field] ? ' — signed' : ' — not signed'}</span>
                </Badge>
              ),
            )}
          </span>
          <a href={d.drive_link} target="_blank" rel="noreferrer noopener" style={OPEN}>
            open
            <span className="hf-sr-only"> {docLabel(d.doc_type)} in Drive</span>
          </a>
        </div>
      ))}

      {canEdit && adding && (
        <DisclosedForm
          opener={opener}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-3)',
            marginTop: 'var(--hf-space-4)',
          }}
        >
          {err && <InlineStatus tone="error">{err}</InlineStatus>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-3)' }}>
            <Field label="Document type" style={{ flex: '1 1 14rem' }}>
              {(control) => (
                <Select
                  {...control}
                  value={docType}
                  onChange={(v) => setDocType(v as DocType)}
                  options={DOC_TYPES.map((t) => ({ value: t.id, label: t.label }))}
                />
              )}
            </Field>
            <Field label="Drive link" required error={linkErr} style={{ flex: '2 1 16rem' }}>
              <TextInput
                dense
                type="url"
                value={link}
                onChange={setLink}
                placeholder="https://drive.google.com/…"
              />
            </Field>
          </div>
          <div
            role="group"
            aria-label="Signatures so far"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-4)' }}
          >
            {triPart && <Checkbox label="Company signed" checked={company} onChange={setCompany} />}
            <Checkbox
              label={triPart ? 'Individual signed' : 'Signed'}
              checked={individual}
              onChange={setIndividual}
            />
            {triPart && <Checkbox label="Witnessed" checked={witnessed} onChange={setWitnessed} />}
          </div>
          {docType === 'handbook_ack' && (
            <div style={NOTE}>
              {current
                ? `Signs handbook ${current.version} — the version current for this role.`
                : 'No handbook issued for this role yet.'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void add()}>
              Save document
            </Button>
            <Button variant="ghost" size="sm" onClick={close}>
              Cancel
            </Button>
          </div>
        </DisclosedForm>
      )}
    </PCard>
  );
}

/** Handbook versions per role, and who is behind on them. */
export function RoleHandbooksCard({
  handbooks,
  roles,
  behind,
  canEdit,
  onChanged,
}: {
  handbooks: RoleHandbook[];
  roles: { id: string; label: string }[];
  /** role id → how many active people have not acknowledged the current version. */
  behind: Record<string, number>;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const { tz } = useOrgDates();
  const [adding, setAdding] = useState(false);
  const opener = useId();
  const [roleId, setRoleId] = useState(roles[0]?.id ?? 'member');
  const [version, setVersion] = useState('');
  const [link, setLink] = useState('');
  const [errors, setErrors] = useState<{ version?: string; link?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setAdding(false);
    setVersion('');
    setLink('');
    setErrors({});
    setErr(null);
  };

  const add = async () => {
    const found: typeof errors = {};
    if (!version.trim()) found.version = 'Name the version, e.g. v3.';
    if (!link.trim()) found.link = 'Paste the Drive link to the handbook.';
    else if (!isLink(link)) found.link = 'That is not a link. It should start with https://';
    setErrors(found);
    if (found.version || found.link) return;
    setBusy(true);
    setErr(null);
    const { error } = await addHandbook({
      roleId,
      version,
      // The org's today, not the UTC date: late in the evening west of GMT a
      // handbook was issued "tomorrow".
      issuedOn: orgDateKey(Date.now(), tz),
      driveLink: link,
    });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    toast.push({
      tone: 'success',
      message: `Issued handbook ${version.trim()} for ${roles.find((r) => r.id === roleId)?.label ?? roleId}.`,
    });
    close();
    onChanged();
  };

  // Newest per role — the one the gate actually checks against.
  const currentByRole = new Map<string, RoleHandbook>();
  for (const h of handbooks) if (!currentByRole.has(h.role_id)) currentByRole.set(h.role_id, h);

  return (
    <PCard
      label="Role handbooks"
      right={
        canEdit &&
        !adding && (
          <span id={opener}>
            <Button variant="secondary" size="sm" icon="plus" onClick={() => setAdding(true)}>
              Issue version
            </Button>
          </span>
        )
      }
    >
      {currentByRole.size === 0 && (
        <div style={NOTE}>
          None issued. Nobody can be invited until the role they are hired into has one.
        </div>
      )}
      {[...currentByRole.values()].map((h) => {
        const role = roles.find((r) => r.id === h.role_id)?.label ?? h.role_id;
        return (
          <div key={h.id} style={ROW}>
            <span style={{ color: 'var(--hf-ink-2)', flex: '1 1 8rem', minWidth: 0 }}>{role}</span>
            <Mono>{h.version}</Mono>
            {/* Waiting on people to acknowledge it: an object's state, inline
                (UI-CONVENTIONS §1), not a queue count. */}
            {behind[h.role_id] > 0 && <Badge tone="warning">{behind[h.role_id]} behind</Badge>}
            <a href={h.drive_link} target="_blank" rel="noreferrer noopener" style={OPEN}>
              open
              <span className="hf-sr-only"> the {role} handbook in Drive</span>
            </a>
          </div>
        );
      })}

      {canEdit && adding && (
        <DisclosedForm
          opener={opener}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-3)',
            marginTop: 'var(--hf-space-4)',
          }}
        >
          {err && <InlineStatus tone="error">{err}</InlineStatus>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-3)' }}>
            <Field label="Role" style={{ flex: '1 1 10rem' }}>
              {(control) => (
                <Select
                  {...control}
                  value={roleId}
                  onChange={setRoleId}
                  options={roles.map((r) => ({ value: r.id, label: r.label }))}
                />
              )}
            </Field>
            <Field label="Version" required error={errors.version} style={{ flex: '1 1 6rem' }}>
              <TextInput dense value={version} onChange={setVersion} placeholder="v3" />
            </Field>
            <Field label="Drive link" required error={errors.link} style={{ flex: '2 1 16rem' }}>
              <TextInput
                dense
                type="url"
                value={link}
                onChange={setLink}
                placeholder="https://drive.google.com/…"
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void add()}>
              Issue handbook
            </Button>
            <Button variant="ghost" size="sm" onClick={close}>
              Cancel
            </Button>
          </div>
        </DisclosedForm>
      )}
    </PCard>
  );
}

/**
 * The three gates, in the invite modal. Shows WHICH piece of paper is missing —
 * "refused" on its own sends somebody hunting through three tables.
 */
export function GatePanel({
  gate,
  email,
  onBypassed,
}: {
  gate: Gate | null;
  email: string;
  onBypassed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  if (!gate) return null;

  // The bypass is a permanent exception against a named person. It was taken
  // through window.prompt, whose blank answer returned in silence (HRA-08).
  const bypass = async (reason: string) => {
    setConfirming(false);
    setBusy(true);
    setErr(null);
    const { error } = await bypassGate(email, reason);
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onBypassed();
  };

  // Inside the invite dialog: the flat panel, like any group in a surface.
  return (
    <div
      className="hf-panel"
      style={{ padding: 'var(--hf-space-3)', borderRadius: 'var(--hf-radius-md)' }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--hf-space-2)',
          marginBottom: 'var(--hf-space-2)',
          fontSize: 'var(--hf-text-sm)',
          color: 'var(--hf-ink-2)',
        }}
      >
        {/* No glyph. The lock means a month that can no longer change
            (UI-CONVENTIONS, Icons), and a check here would read as "passed"
            over a list that may not have: the checklist marks each item. */}
        <span>Before they can be invited</span>
        {gate.exception && <Badge tone="neutral">exception filed</Badge>}
      </div>
      <Checklist
        items={[
          { label: 'A current handbook for this role', ok: gate.handbook_exists },
          { label: 'IP & confidentiality signed by all three', ok: gate.ip_signed },
          { label: 'Handbook acknowledgement, current version', ok: gate.ack_signed },
        ]}
      />
      {err && (
        <div style={{ marginTop: 'var(--hf-space-2)' }}>
          <InlineStatus tone="error">{err}</InlineStatus>
        </div>
      )}
      {!gate.passed && (
        <div style={{ marginTop: 'var(--hf-space-2)' }}>
          <Button variant="ghost" size="sm" loading={busy} onClick={() => setConfirming(true)}>
            Invite anyway — file an exception
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        title={`Invite ${email.trim() || 'them'} without the paperwork?`}
        consequence={[
          'An exception is recorded against them, with your name and this reason.',
          'It stays on the record permanently, and on the org-wide exceptions list.',
        ]}
        confirmLabel="File the exception"
        requireReason={{ label: 'Why are they starting without it?', minLength: 3 }}
        onCancel={() => setConfirming(false)}
        onConfirm={(reason) => reason && void bypass(reason)}
      />
    </div>
  );
}
